import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Layers, Plus, Trash2, Edit3, ChevronRight, Eye, Globe, Shield, FileText
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import RichTextEditor from '../../components/RichTextEditor';
import AdminLayout from './AdminLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Template Preview Modal
function TemplatePreview({ template, open, onClose }) {
    if (!template) return null;
    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Template Preview</DialogTitle>
                    <DialogDescription>This is how a Deal Room created from this template will appear</DialogDescription>
                </DialogHeader>
                <div className="border border-stone-200 rounded-xl overflow-hidden mt-2">
                    <div className="bg-gradient-to-r from-stone-900 to-stone-700 p-6 text-white">
                        <h2 className="text-xl font-bold">{template.name}</h2>
                        {template.description && <p className="opacity-80 text-sm mt-1">{template.description}</p>}
                    </div>
                    <div className="p-6 bg-stone-50">
                        {(!template.sections || template.sections.length === 0) ? (
                            <p className="text-center text-stone-400 py-8">No sections in this template</p>
                        ) : (
                            <div className="space-y-6">
                                {template.sections.map((section, idx) => (
                                    <div key={idx} className="bg-white rounded-xl p-5 border border-stone-200">
                                        <h3 className="font-semibold text-stone-900 mb-1">{section.name}</h3>
                                        {section.description && <p className="text-sm text-stone-500 mb-4">{section.description}</p>}
                                        {section.items && section.items.length > 0 ? (
                                            <div className="space-y-2">
                                                {section.items.map((item, iIdx) => (
                                                    <div key={iIdx} className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg">
                                                        {item.content_type === 'richtext' ? <FileText className="w-5 h-5 text-emerald-600" /> :
                                                         item.content_type === 'link' ? <Globe className="w-5 h-5 text-blue-600" /> :
                                                         <FileText className="w-5 h-5 text-stone-700" />}
                                                        <div className="flex-1">
                                                            <p className="text-sm font-medium text-stone-800">{item.title}</p>
                                                            <p className="text-xs text-stone-400 capitalize">{item.content_type}</p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-sm text-stone-400 italic">Empty section — content to be added when creating a Deal Room</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// Template Builder — full page detail view for creating/editing a template
function TemplateBuilder({ template, groups, users, onSave, onCancel }) {
    const [form, setForm] = useState({
        name: template?.name || '',
        description: template?.description || '',
        access_type: template?.access_type || 'all',
        allowed_group_ids: template?.allowed_group_ids || [],
        allowed_user_ids: template?.allowed_user_ids || [],
        sections: template?.sections || [],
    });
    const [expandedSection, setExpandedSection] = useState(null);
    const [showAddSection, setShowAddSection] = useState(false);
    const [newSection, setNewSection] = useState({ name: '', description: '' });
    const [showAddItem, setShowAddItem] = useState(false);
    const [addItemToIdx, setAddItemToIdx] = useState(null);
    const [newItem, setNewItem] = useState({ title: '', content_type: 'richtext', rich_content: '' });
    const [showPreview, setShowPreview] = useState(false);
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        if (!form.name.trim()) return toast.error('Please enter a template name');
        setSaving(true);
        try {
            await onSave(form);
        } finally {
            setSaving(false);
        }
    };

    const addSection = () => {
        if (!newSection.name.trim()) return toast.error('Enter a section name');
        setForm(prev => ({ ...prev, sections: [...prev.sections, { ...newSection, items: [] }] }));
        setNewSection({ name: '', description: '' });
        setShowAddSection(false);
        setExpandedSection(form.sections.length);
    };

    const removeSection = (idx) => {
        setForm(prev => ({ ...prev, sections: prev.sections.filter((_, i) => i !== idx) }));
        if (expandedSection === idx) setExpandedSection(null);
    };

    const updateSection = (idx, field, value) => {
        setForm(prev => ({
            ...prev,
            sections: prev.sections.map((s, i) => i === idx ? { ...s, [field]: value } : s),
        }));
    };

    const openAddItem = (sectionIdx) => {
        setAddItemToIdx(sectionIdx);
        setNewItem({ title: '', content_type: 'richtext', rich_content: '' });
        setShowAddItem(true);
    };

    const addItem = () => {
        if (!newItem.title.trim()) return toast.error('Enter an item title');
        setForm(prev => ({
            ...prev,
            sections: prev.sections.map((s, i) =>
                i === addItemToIdx ? { ...s, items: [...(s.items || []), { ...newItem }] } : s
            ),
        }));
        setNewItem({ title: '', content_type: 'richtext', rich_content: '' });
        setShowAddItem(false);
    };

    const removeItem = (sectionIdx, itemIdx) => {
        setForm(prev => ({
            ...prev,
            sections: prev.sections.map((s, i) =>
                i === sectionIdx ? { ...s, items: s.items.filter((_, j) => j !== itemIdx) } : s
            ),
        }));
    };

    const moveSection = (idx, direction) => {
        const newIdx = idx + direction;
        if (newIdx < 0 || newIdx >= form.sections.length) return;
        setForm(prev => {
            const sections = [...prev.sections];
            [sections[idx], sections[newIdx]] = [sections[newIdx], sections[idx]];
            return { ...prev, sections };
        });
        setExpandedSection(newIdx);
    };

    const totalItems = form.sections.reduce((sum, s) => sum + (s.items?.length || 0), 0);

    return (
        <div data-testid="template-builder">
            {/* Builder Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <button onClick={onCancel} className="p-2 hover:bg-stone-100 rounded-lg text-stone-400 hover:text-stone-600" data-testid="template-builder-back">
                        <ChevronRight className="w-5 h-5 rotate-180" />
                    </button>
                    <div>
                        <h2 className="text-lg font-semibold text-stone-900">
                            {template ? 'Edit Template' : 'Create Template'}
                        </h2>
                        <p className="text-sm text-stone-500">
                            {form.sections.length} sections, {totalItems} content items
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={() => setShowPreview(true)} data-testid="template-preview-btn">
                        <Eye className="w-4 h-4 mr-2" />Preview
                    </Button>
                    <Button variant="outline" onClick={onCancel}>Cancel</Button>
                    <Button onClick={handleSave} disabled={saving} className="bg-stone-900 hover:bg-stone-800" data-testid="template-save-btn">
                        {saving ? 'Saving...' : template ? 'Save Changes' : 'Create Template'}
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-12 gap-6">
                {/* Left: Settings Panel */}
                <div className="col-span-4 space-y-4">
                    <div className="bg-white rounded-xl border border-stone-200 p-5">
                        <h3 className="font-semibold text-stone-900 mb-4">Template Details</h3>
                        <div className="space-y-3">
                            <div>
                                <Label>Name *</Label>
                                <Input value={form.name} onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))} placeholder="e.g., Enterprise Sales Template" className="mt-1" data-testid="template-name-input" />
                            </div>
                            <div>
                                <Label>Description</Label>
                                <Textarea value={form.description} onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))} placeholder="Describe when to use this template..." className="mt-1" rows={3} data-testid="template-desc-input" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl border border-stone-200 p-5">
                        <h3 className="font-semibold text-stone-900 mb-4">Access Control</h3>
                        <Select value={form.access_type} onValueChange={(v) => setForm(prev => ({ ...prev, access_type: v }))}>
                            <SelectTrigger data-testid="template-access-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Users</SelectItem>
                                <SelectItem value="groups">Specific Groups</SelectItem>
                                <SelectItem value="users">Specific Users</SelectItem>
                            </SelectContent>
                        </Select>

                        {form.access_type === 'groups' && (
                            <div className="mt-3 space-y-1 max-h-48 overflow-y-auto">
                                {groups.map(group => (
                                    <label key={group.group_id} className="flex items-center gap-2 p-2 hover:bg-stone-50 rounded cursor-pointer">
                                        <input type="checkbox" checked={form.allowed_group_ids.includes(group.group_id)}
                                            onChange={(e) => setForm(prev => ({
                                                ...prev, allowed_group_ids: e.target.checked
                                                    ? [...prev.allowed_group_ids, group.group_id]
                                                    : prev.allowed_group_ids.filter(id => id !== group.group_id)
                                            }))}
                                            className="rounded border-stone-300" />
                                        <span className="text-sm">{group.name}</span>
                                        <span className="text-xs text-stone-400">({group.members?.length || 0})</span>
                                    </label>
                                ))}
                                {groups.length === 0 && <p className="text-xs text-stone-400 py-2">No groups created yet</p>}
                            </div>
                        )}

                        {form.access_type === 'users' && (
                            <div className="mt-3 space-y-1 max-h-48 overflow-y-auto">
                                {users.map(user => (
                                    <label key={user.user_id} className="flex items-center gap-2 p-2 hover:bg-stone-50 rounded cursor-pointer">
                                        <input type="checkbox" checked={form.allowed_user_ids.includes(user.user_id)}
                                            onChange={(e) => setForm(prev => ({
                                                ...prev, allowed_user_ids: e.target.checked
                                                    ? [...prev.allowed_user_ids, user.user_id]
                                                    : prev.allowed_user_ids.filter(id => id !== user.user_id)
                                            }))}
                                            className="rounded border-stone-300" />
                                        <span className="text-sm">{user.name}</span>
                                        <span className="text-xs text-stone-400">{user.email}</span>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Right: Sections Builder */}
                <div className="col-span-8">
                    <div className="bg-white rounded-xl border border-stone-200 p-5">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-semibold text-stone-900">Sections & Content</h3>
                            <Button size="sm" onClick={() => setShowAddSection(true)} data-testid="add-section-btn">
                                <Plus className="w-4 h-4 mr-1" /> Add Section
                            </Button>
                        </div>

                        {form.sections.length === 0 ? (
                            <div className="border-2 border-dashed border-stone-200 rounded-xl p-10 text-center">
                                <Layers className="w-10 h-10 mx-auto text-stone-300 mb-3" />
                                <p className="text-stone-500 mb-1">No sections yet</p>
                                <p className="text-sm text-stone-400 mb-4">Add sections to define the template structure that salespeople will use</p>
                                <Button variant="outline" onClick={() => setShowAddSection(true)}>Add First Section</Button>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {form.sections.map((section, idx) => {
                                    const isExpanded = expandedSection === idx;
                                    return (
                                        <div key={idx} className={`border rounded-xl transition-colors ${isExpanded ? 'border-stone-300 bg-stone-100/30' : 'border-stone-200'}`} data-testid={`template-section-${idx}`}>
                                            {/* Section Header */}
                                            <div className="flex items-center gap-3 p-4 cursor-pointer" onClick={() => setExpandedSection(isExpanded ? null : idx)}>
                                                <ChevronRight className={`w-4 h-4 text-stone-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-semibold text-stone-800">{section.name}</p>
                                                    <p className="text-xs text-stone-400">
                                                        {section.items?.length || 0} items
                                                        {section.description ? ` — ${section.description}` : ''}
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                                                    <button onClick={() => moveSection(idx, -1)} disabled={idx === 0} className="p-1.5 hover:bg-white rounded text-stone-400 hover:text-stone-600 disabled:opacity-30" title="Move up">
                                                        <ChevronRight className="w-3.5 h-3.5 -rotate-90" />
                                                    </button>
                                                    <button onClick={() => moveSection(idx, 1)} disabled={idx === form.sections.length - 1} className="p-1.5 hover:bg-white rounded text-stone-400 hover:text-stone-600 disabled:opacity-30" title="Move down">
                                                        <ChevronRight className="w-3.5 h-3.5 rotate-90" />
                                                    </button>
                                                    <button onClick={() => openAddItem(idx)} className="p-1.5 hover:bg-white rounded text-stone-400 hover:text-stone-700" title="Add item">
                                                        <Plus className="w-3.5 h-3.5" />
                                                    </button>
                                                    <button onClick={() => removeSection(idx)} className="p-1.5 hover:bg-white rounded text-stone-400 hover:text-red-500" title="Delete section">
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Expanded: show items */}
                                            {isExpanded && (
                                                <div className="px-4 pb-4">
                                                    {/* Inline edit section name/desc */}
                                                    <div className="grid grid-cols-2 gap-3 mb-4 pb-3 border-b border-stone-200">
                                                        <div>
                                                            <Label className="text-xs">Section Name</Label>
                                                            <Input value={section.name} onChange={(e) => updateSection(idx, 'name', e.target.value)} className="mt-1 h-8 text-sm" />
                                                        </div>
                                                        <div>
                                                            <Label className="text-xs">Description</Label>
                                                            <Input value={section.description || ''} onChange={(e) => updateSection(idx, 'description', e.target.value)} className="mt-1 h-8 text-sm" placeholder="Optional" />
                                                        </div>
                                                    </div>

                                                    {(!section.items || section.items.length === 0) ? (
                                                        <div className="text-center py-4">
                                                            <p className="text-sm text-stone-400 mb-2">No content items</p>
                                                            <Button size="sm" variant="outline" onClick={() => openAddItem(idx)}>
                                                                <Plus className="w-3 h-3 mr-1" /> Add Item
                                                            </Button>
                                                        </div>
                                                    ) : (
                                                        <div className="space-y-2">
                                                            {section.items.map((item, iIdx) => (
                                                                <div key={iIdx} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-stone-100 group" data-testid={`template-item-${idx}-${iIdx}`}>
                                                                    <div className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 ${
                                                                        item.content_type === 'richtext' ? 'bg-emerald-50' :
                                                                        item.content_type === 'link' ? 'bg-blue-50' : 'bg-stone-100'
                                                                    }`}>
                                                                        {item.content_type === 'richtext' ? <FileText className="w-4 h-4 text-emerald-600" /> :
                                                                         item.content_type === 'link' ? <Globe className="w-4 h-4 text-blue-600" /> :
                                                                         <FileText className="w-4 h-4 text-stone-700" />}
                                                                    </div>
                                                                    <div className="flex-1 min-w-0">
                                                                        <p className="text-sm font-medium text-stone-800">{item.title}</p>
                                                                        <p className="text-xs text-stone-400 capitalize">{item.content_type === 'richtext' ? 'Rich Text' : item.content_type}</p>
                                                                    </div>
                                                                    {item.rich_content && (
                                                                        <span className="text-xs px-2 py-0.5 bg-emerald-50 text-emerald-600 rounded-full">Has content</span>
                                                                    )}
                                                                    <button onClick={() => removeItem(idx, iIdx)} className="p-1.5 opacity-0 group-hover:opacity-100 hover:bg-red-50 rounded text-stone-400 hover:text-red-500 transition-opacity">
                                                                        <Trash2 className="w-3.5 h-3.5" />
                                                                    </button>
                                                                </div>
                                                            ))}
                                                            <Button size="sm" variant="ghost" onClick={() => openAddItem(idx)} className="w-full text-stone-400 hover:text-stone-700">
                                                                <Plus className="w-3 h-3 mr-1" /> Add Item
                                                            </Button>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Add Section Dialog */}
            <Dialog open={showAddSection} onOpenChange={setShowAddSection}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Add Section</DialogTitle>
                        <DialogDescription>Create a new section for this template</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 py-3">
                        <div>
                            <Label>Section Name *</Label>
                            <Input value={newSection.name} onChange={(e) => setNewSection(prev => ({ ...prev, name: e.target.value }))} placeholder="e.g., Getting Started" className="mt-1" data-testid="new-section-name" />
                        </div>
                        <div>
                            <Label>Description (optional)</Label>
                            <Input value={newSection.description} onChange={(e) => setNewSection(prev => ({ ...prev, description: e.target.value }))} placeholder="Brief description" className="mt-1" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAddSection(false)}>Cancel</Button>
                        <Button onClick={addSection} className="bg-stone-900 hover:bg-stone-800" data-testid="confirm-add-section">Add Section</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Add Content Item Dialog */}
            <Dialog open={showAddItem} onOpenChange={setShowAddItem}>
                <DialogContent className={newItem.content_type === 'richtext' ? 'sm:max-w-3xl' : 'sm:max-w-lg'}>
                    <DialogHeader>
                        <DialogTitle>Add Content Item</DialogTitle>
                        <DialogDescription>
                            Adding to: {addItemToIdx !== null ? form.sections[addItemToIdx]?.name : ''}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 py-3">
                        <div>
                            <Label>Title *</Label>
                            <Input value={newItem.title} onChange={(e) => setNewItem(prev => ({ ...prev, title: e.target.value }))} placeholder="e.g., Welcome Message" className="mt-1" data-testid="new-item-title" />
                        </div>
                        <div>
                            <Label>Content Type</Label>
                            <Select value={newItem.content_type} onValueChange={(v) => setNewItem(prev => ({ ...prev, content_type: v }))}>
                                <SelectTrigger className="mt-1" data-testid="new-item-type">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="richtext">Rich Text</SelectItem>
                                    <SelectItem value="document">Document Placeholder</SelectItem>
                                    <SelectItem value="link">External Link</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        {newItem.content_type === 'richtext' && (
                            <div>
                                <Label>Content (optional — can be filled later)</Label>
                                <div className="mt-1">
                                    <RichTextEditor content={newItem.rich_content} onChange={(html) => setNewItem(prev => ({ ...prev, rich_content: html }))} minHeight="200px" showContentLibrary={true} />
                                </div>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAddItem(false)}>Cancel</Button>
                        <Button onClick={addItem} className="bg-stone-900 hover:bg-stone-800" data-testid="confirm-add-item">Add Item</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Preview */}
            <TemplatePreview template={form} open={showPreview} onClose={() => setShowPreview(false)} />
        </div>
    );
}

// Template List + Builder wrapper
function TemplatesTab() {
    const { getAuthHeader } = useAuth();
    const [templates, setTemplates] = useState([]);
    const [groups, setGroups] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [mode, setMode] = useState('list');
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [previewTemplate, setPreviewTemplate] = useState(null);

    useEffect(() => { loadData(); }, []);

    const loadData = async () => {
        try {
            const [templatesRes, groupsRes, usersRes] = await Promise.all([
                axios.get(`${API}/admin/templates`, { headers: getAuthHeader() }),
                axios.get(`${API}/admin/user-groups`, { headers: getAuthHeader() }),
                axios.get(`${API}/users`, { headers: getAuthHeader() }),
            ]);
            setTemplates(templatesRes.data);
            setGroups(groupsRes.data);
            setUsers(usersRes.data);
        } catch { toast.error('Failed to load templates'); }
        finally { setLoading(false); }
    };

    const handleCreate = async (formData) => {
        await axios.post(`${API}/admin/templates`, formData, { headers: getAuthHeader() });
        toast.success('Template created');
        setMode('list');
        loadData();
    };

    const handleUpdate = async (formData) => {
        await axios.put(`${API}/admin/templates/${editingTemplate.template_id}`, formData, { headers: getAuthHeader() });
        toast.success('Template updated');
        setMode('list');
        setEditingTemplate(null);
        loadData();
    };

    const handleDelete = async (templateId) => {
        if (!window.confirm('Delete this template?')) return;
        try {
            await axios.delete(`${API}/admin/templates/${templateId}`, { headers: getAuthHeader() });
            toast.success('Template deleted');
            loadData();
        } catch { toast.error('Failed to delete template'); }
    };

    if (loading) return <div className="flex items-center justify-center h-40"><div className="w-6 h-6 border-2 border-stone-700 border-t-transparent rounded-full animate-spin" /></div>;

    // Builder view
    if (mode === 'create') {
        return <TemplateBuilder groups={groups} users={users} onSave={handleCreate} onCancel={() => setMode('list')} />;
    }
    if (mode === 'edit' && editingTemplate) {
        return <TemplateBuilder template={editingTemplate} groups={groups} users={users} onSave={handleUpdate} onCancel={() => { setMode('list'); setEditingTemplate(null); }} />;
    }

    // List view
    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="text-lg font-semibold text-stone-900">Deal Room Templates</h2>
                    <p className="text-sm text-stone-500">Create templates that sales teams can use to quickly set up deal rooms</p>
                </div>
                <Button onClick={() => setMode('create')} className="bg-stone-900 hover:bg-stone-800" data-testid="create-template-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Template
                </Button>
            </div>

            {templates.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed border-stone-200 rounded-xl">
                    <Layers className="w-12 h-12 mx-auto text-stone-300 mb-4" />
                    <h3 className="text-lg font-semibold text-stone-700 mb-2">No templates yet</h3>
                    <p className="text-stone-500 mb-4">Create templates with pre-configured sections and content</p>
                    <Button onClick={() => setMode('create')} data-testid="create-first-template-btn">Create Your First Template</Button>
                </div>
            ) : (
                <div className="space-y-3">
                    {templates.map(template => (
                        <div key={template.template_id} className="bg-white border border-stone-200 rounded-xl p-5 hover:shadow-md transition-shadow" data-testid={`template-card-${template.template_id}`}>
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-xl bg-stone-100 flex items-center justify-center flex-shrink-0">
                                    <Layers className="w-6 h-6 text-stone-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-0.5">
                                        <h3 className="font-semibold text-stone-900">{template.name}</h3>
                                        <span className={`text-xs px-2 py-0.5 rounded-full ${template.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-stone-100 text-stone-500'}`}>
                                            {template.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                    </div>
                                    {template.description && <p className="text-sm text-stone-500 truncate">{template.description}</p>}
                                    <div className="flex items-center gap-4 mt-1.5 text-xs text-stone-400">
                                        <span className="flex items-center gap-1"><Layers className="w-3.5 h-3.5" />{template.sections?.length || 0} sections</span>
                                        <span className="flex items-center gap-1"><FileText className="w-3.5 h-3.5" />{template.sections?.reduce((sum, s) => sum + (s.items?.length || 0), 0) || 0} items</span>
                                        <span className="flex items-center gap-1">
                                            {template.access_type === 'all' ? <Globe className="w-3.5 h-3.5" /> : <Shield className="w-3.5 h-3.5" />}
                                            {template.access_type === 'all' ? 'All Users' :
                                             template.access_type === 'groups' ? `${template.allowed_group_ids?.length || 0} groups` :
                                             `${template.allowed_user_ids?.length || 0} users`}
                                        </span>
                                        {template.created_by_name && <span>by {template.created_by_name}</span>}
                                    </div>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <Button variant="outline" size="sm" onClick={() => setPreviewTemplate(template)} data-testid={`preview-template-${template.template_id}`}>
                                        <Eye className="w-4 h-4 mr-1.5" />Preview
                                    </Button>
                                    <Button variant="outline" size="sm" onClick={() => { setEditingTemplate(template); setMode('edit'); }} data-testid={`edit-template-${template.template_id}`}>
                                        <Edit3 className="w-4 h-4 mr-1.5" />Edit
                                    </Button>
                                    <button onClick={() => handleDelete(template.template_id)} className="p-2 hover:bg-red-50 rounded-lg text-stone-400 hover:text-red-500" data-testid={`delete-template-${template.template_id}`}>
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Preview Modal */}
            <TemplatePreview template={previewTemplate} open={!!previewTemplate} onClose={() => setPreviewTemplate(null)} />
        </div>
    );
}

export default function AdminDealRoomTemplatesPage() {
    return (
        <AdminLayout title="Deal Room Templates" description="Create templates for deal room setup">
            <TemplatesTab />
        </AdminLayout>
    );
}
